// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import { Box } from 'grommet'
import PropTypes from 'prop-types'
import { useEffect, useReducer, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import { useReactOidc } from '@axa-fr/react-oidc-context'
import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  Button,
  CCSForm,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  Notification,
  Typography
} from '../../../../components'
import { useCCSContext } from '../../../../context/ccs-context'
import { get, getErrorMessage, put } from '../../../../utils/api-utils'
import { displayApiError } from '../../../../utils/error-handling-utils'
import { useVisibilityContext } from '../../../../context/visibility-context'
import { updateV2RBACPolicies } from '../../../../utils/rbac-api-utils'
import {
  WORKSPACE_SCOPES,
  SUBJECT_PREFIX,
  MSP_SCOPE_ASSIGNMENT_TYPE,
  TENANT_WORKSPACE_SCOPES
} from '../constants'
import {
  getScopesFromStateInfo,
  getSubjectName,
  isCCSRole,
  validateScopeSelection,
  checkIfMSPScopesAreOneRoleApp
} from '../utils'
import { isAuthzV2London, isMSP } from '../../../../utils/feature-flag-utils'

import MSPScopeSelection from './MSPScopeSelection'
import WorkspaceScopeSelection from './WorkspaceScopeSelection'
import OneRoleAppWarningNotification from './OneRoleAppWarningNotification'

const EditRoleAssignmentSidePanel = ({
  onSetOpen = () => {},
  refreshParent = () => {},
  roleAssignment,
  subject,
  setNotificationInfo = () => {},
  userRAs
}) => {
  const { oidcUser } = useReactOidc()
  const LDFlags = useFlags()
  const isV2London = isAuthzV2London(LDFlags)
  const { t } = useTranslation(['common', 'iam', 'manage'])
  const [loading, setLoading] = useState(false)
  const [scopeError, setScopeError] = useState(false)
  const [mspScopeError, setMspScopeError] = useState(false)
  const [wkspcGroupScopeError, setWkspcGroupScopeError] = useState(false)
  const [scopeGroupInstanceError, setScopeGroupInstanceError] = useState('')
  const [wkspcGroupInstanceError, setWkspcGroupInstanceError] = useState('')
  const [apiError, setApiError] = useState(null)
  const { workSpaceGrn } = useCCSContext()
  const { csrfToken } = useCCSContext()
  const { dispatchVisibilityContext } = useVisibilityContext()
  // For one role applications
  const [serviceName, setServiceName] = useState(null)
  const [hasConflictingOneRoleAppRA, setHasConflictingOneRoleAppRA] =
    useState(false)
  const [hasConflictingMSPOneRoleAppRA, setHasConflictingMSPOneRoleAppRA] =
    useState(false)
  const [hasMSPOneRoleAppScope, setHasMSPOneRoleAppScope] = useState(false)
  const [hasCustomerOneRoleAppScope, setHasCustomerOneRoleAppScope] =
    useState(false)

  const initState = {
    scope: '',
    customerWorkspaceScope: '',
    scopeGroupInstances: null,
    scopeGroupInstanceOptions: [],
    workspaceGroupInstances: null,
    workspaceGroupInstanceOptions: []
  }

  const [state, dispatch] = useReducer((currentState, action) => {
    switch (action?.type) {
      case 'CHANGE_FIELD':
        return { ...currentState, [action?.field]: action?.value }
      default:
        return currentState
    }
  }, initState)

  const clearScopeErrors = () => {
    setScopeGroupInstanceError('')
    setScopeError('')
    setMspScopeError('')
  }

  const clearCustomerWorkspaceScopeErrors = () => {
    setWkspcGroupScopeError('')
    setWkspcGroupInstanceError('')
    setMspScopeError('')
  }

  const handleDispatch = async (option, field) => {
    dispatch({
      value: option,
      field,
      type: 'CHANGE_FIELD'
    })
  }

  const getServicesProvisioned = async () => {
    let provisionsService = []
    try {
      const response = await get(
        '/ui-doorway/ui/v1/applications/provisions',
        {
          provision_status: 'PROVISIONED'
        },
        oidcUser?.access_token
      )
      provisionsService = response?.data?.provisions
    } catch (error) {
      setApiError(error)
    }
    return provisionsService
  }

  const getOneRoleAppIds = (provisions) => {
    return provisions
      ?.filter((app) => app?.support_one_role_only)
      .map((service) => service?.application_id)
  }

  const isOneRoleAppAssgConflicted = (
    roleAssg,
    selectedRoleServiceId,
    selectedRoleId
  ) => {
    return (
      (roleAssg?.service_id === selectedRoleServiceId ||
        roleAssg?.custom_role_service_id === selectedRoleServiceId) &&
      roleAssg?.role_id !== selectedRoleId
    )
  }

  const oneRoleAppCheck = async (roleAssg, userRoleAssignments) => {
    const provisionedServices = await getServicesProvisioned()
    const selectedRoleServiceId =
      roleAssg?.service_id || roleAssg?.custom_role_service_id
    const selectedRoleId = roleAssg?.role_id

    // checking user's existing role assignments to see if there is a one role app
    if (subject?.type === 'USER') {
      const oneRoleIds = await getOneRoleAppIds(provisionedServices)

      // checking if selected role assignment is a one role app
      if (oneRoleIds?.includes(selectedRoleServiceId)) {
        // Determine if currently selected one role app has a conflict with user's existing role assignments
        // (same service id)
        const assgConflicted = userRoleAssignments?.find((ra) => {
          return (
            ra &&
            isOneRoleAppAssgConflicted(
              ra,
              selectedRoleServiceId,
              selectedRoleId
            )
          )
        })

        if (assgConflicted) {
          setHasConflictingOneRoleAppRA(true)
          setServiceName(
            roleAssg?.service_name || roleAssg?.custom_role_service_name
          )

          if (isMSP() && isV2London) {
            checkIfMSPScopesAreOneRoleApp(
              assgConflicted,
              setHasMSPOneRoleAppScope,
              setHasCustomerOneRoleAppScope,
              setHasConflictingMSPOneRoleAppRA
            )
          }
        }
      }
    }
  }

  // populate scope option/values from existing role assignments
  useEffect(() => {
    const scopes = roleAssignment?.scopes
    let scopeOption
    const workspaceScopes = []
    const customerWkspceScopes = []
    scopes?.forEach((scope) => {
      if (
        scope?.scopeType === MSP_SCOPE_ASSIGNMENT_TYPE?.MSP_SCOPE_GROUP ||
        scope?.scopeType === MSP_SCOPE_ASSIGNMENT_TYPE?.MSP_WORKSPACE
      ) {
        workspaceScopes?.push(scope)
      } else if (
        scope?.scopeType ===
          MSP_SCOPE_ASSIGNMENT_TYPE?.CUSTOMER_WORKSPACE_GROUP ||
        scope?.scopeType === MSP_SCOPE_ASSIGNMENT_TYPE?.CUSTOMER_ALL_CUSTOMERS
      ) {
        customerWkspceScopes?.push(scope)
      }
    })
    if (isEmpty(workspaceScopes)) {
      scopeOption = WORKSPACE_SCOPES?.NONE
    } else if (
      workspaceScopes?.length === 1 &&
      isEmpty(workspaceScopes[0]?.scopeId)
    ) {
      scopeOption = WORKSPACE_SCOPES?.ENTIRE
    } else {
      scopeOption = WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS
    }
    handleDispatch(scopeOption, 'scope')
    handleDispatch(workspaceScopes, 'scopeGroupInstances')
    if (isMSP()) {
      let customerWkspceScopeOption
      if (isEmpty(customerWkspceScopes)) {
        customerWkspceScopeOption = TENANT_WORKSPACE_SCOPES?.NONE
      } else if (
        customerWkspceScopes?.length === 1 &&
        isEmpty(customerWkspceScopes[0]?.scopeId)
      ) {
        customerWkspceScopeOption = TENANT_WORKSPACE_SCOPES?.ALL
      } else {
        customerWkspceScopeOption =
          TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS
      }
      handleDispatch(customerWkspceScopeOption, 'customerWorkspaceScope')
      handleDispatch(customerWkspceScopes, 'workspaceGroupInstances')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    oneRoleAppCheck(roleAssignment, userRAs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUpdateRoleAssignment = async () => {
    if (
      validateScopeSelection(
        state,
        t,
        setScopeError,
        setScopeGroupInstanceError,
        setWkspcGroupScopeError,
        setWkspcGroupInstanceError,
        setMspScopeError
      ) &&
      !hasConflictingMSPOneRoleAppRA
    ) {
      const subjectType = subject?.type || roleAssignment?.subject_type
      const subjectId = subject?.value?.id || roleAssignment?.subject_id
      const payload = {
        id: roleAssignment?.id,
        subject: `${
          subject ? SUBJECT_PREFIX[subjectType] : subjectType
        }:${subjectId}`,
        role: roleAssignment?.role_grn,
        scope: getScopesFromStateInfo(state, workSpaceGrn)
      }
      put(
        `/authorization/v2alpha2/role-assignments/${roleAssignment?.id}`,
        payload
      )
        .then(() => {
          // This could affect the current user's permissions the user's role assignment has changed
          updateV2RBACPolicies(dispatchVisibilityContext, csrfToken)
          setNotificationInfo(
            <Typography type="text">
              <Trans>
                {t(
                  'iam:assign_roles_side_panel.edit_role_assignment_scope_notification_desc_v2',
                  {
                    roleName: roleAssignment?.role_name,
                    roleAssignment: t(
                      'common:business_object.role_assignment_capitalized'
                    )
                  }
                )}
              </Trans>
            </Typography>,
            'info',
            t(
              'iam:assign_roles_side_panel.edit_role_assignment_scope_notification_heading',
              {
                roleAssignment: t(
                  'common:business_object.role_assignment_capitalized'
                )
              }
            )
          )
          refreshParent()
        })
        .catch((error) => {
          setNotificationInfo(getErrorMessage(error, t), 'error')
        })
        .finally(() => {
          onSetOpen(false)
          setLoading(false)
        })
    }
  }

  return (
    <>
      {apiError && displayApiError(apiError, t, setApiError)}
      <ModalDialog
        header={
          <ModalHeader
            title={
              <Typography
                type="heading"
                testId="edit-role-assignment-heading"
                level="2"
              >
                {t('iam:assign_roles_side_panel.edit_role_assignment_scope', {
                  roleAssignment: t('common:business_object.role_assignment')
                })}
              </Typography>
            }
            subtitle={
              <Box margin={{ top: 'medium' }}>
                <Typography type="text">
                  <Trans>
                    {t(
                      'iam:assign_roles_side_panel.edit_role_assignment_scope_desc_v2',
                      {
                        roleName: roleAssignment?.role_name,
                        roleAssignment: t(
                          'common:business_object.role_assignment'
                        ),
                        subjectName: subject
                          ? getSubjectName(subject?.value, subject?.type)
                          : roleAssignment?.subject_name ||
                            roleAssignment?.subject_email
                      }
                    )}
                  </Trans>
                </Typography>
              </Box>
            }
            onClose={() => onSetOpen(false)}
          />
        }
        content={
          <Box margin={{ top: 'medium' }}>
            <CCSForm
              onSubmit={handleUpdateRoleAssignment}
              errorMessage=""
              testId="edit-role-assignment-form"
            >
              {isMSP() ? (
                <>
                  <MSPScopeSelection
                    handleSelection={handleDispatch}
                    scopeInfo={state}
                    workspaceScopeError={{
                      scopeError,
                      scopeGroupInstanceError,
                      clearError: clearScopeErrors
                    }}
                    customerWorkspaceScopeError={{
                      wkspcGroupScopeError,
                      wkspcGroupInstanceError,
                      clearError: clearCustomerWorkspaceScopeErrors
                    }}
                    setApiError={setApiError}
                    isCCSRole={isCCSRole(roleAssignment)}
                    hasMSPOneRoleAppScope={hasMSPOneRoleAppScope}
                    hasCustomerOneRoleAppScope={hasCustomerOneRoleAppScope}
                    setHasConflictingMSPOneRoleAppRA={
                      setHasConflictingMSPOneRoleAppRA
                    }
                    hasConflictingOneRoleAppRA={hasConflictingOneRoleAppRA}
                    hasConflictingMSPOneRoleAppRA={
                      hasConflictingMSPOneRoleAppRA
                    }
                    state={state}
                    serviceName={serviceName}
                    subject={subject}
                  />
                  {hasConflictingOneRoleAppRA &&
                    isV2London &&
                    hasConflictingMSPOneRoleAppRA &&
                    !hasMSPOneRoleAppScope &&
                    hasCustomerOneRoleAppScope &&
                    !loading && (
                      <OneRoleAppWarningNotification
                        state={state}
                        serviceName={serviceName}
                        subject={subject}
                      />
                    )}
                  {mspScopeError && (
                    <Box pad={{ top: 'small' }}>
                      <Notification
                        type="inline"
                        message={mspScopeError}
                        status="critical"
                        testId="no-scope-selection-warning"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <WorkspaceScopeSelection
                  handleSelection={handleDispatch}
                  scopeOption={state?.scope}
                  preselectScopeGroups={state?.scopeGroupInstances}
                  scopeError={{
                    scopeError,
                    scopeGroupInstanceError,
                    clearError: clearScopeErrors
                  }}
                  setApiError={setApiError}
                  isCCSRole={isCCSRole(roleAssignment)}
                />
              )}
            </CCSForm>
          </Box>
        }
        footer={
          <ModalFooter
            left={
              <Box
                direction="row"
                gap="medium"
                margin={{ top: 'xsmall' }}
                testId="edit-role-assignment-btn-group"
              >
                <Button
                  primary
                  label={t('manage:save_changes')}
                  type="submit"
                  onClick={() => {
                    handleUpdateRoleAssignment()
                  }}
                  isLoading={loading}
                  disabled={loading}
                  testId="edit-role-assignment-save-btn"
                />
                <Button
                  default
                  label={t('common:cancel')}
                  onClick={() => onSetOpen(false)}
                  testId="edit-role-assignment-cancel-btn"
                />
              </Box>
            }
          />
        }
        position="right"
        height="100%"
        width={{ min: 'full', max: 'medium' }}
        onClose={() => onSetOpen(false)}
        testId="edit-role-assignment-modal"
      />
    </>
  )
}

EditRoleAssignmentSidePanel.propTypes = {
  /**
   * Data received from the role details tab
   */
  roleAssignment: PropTypes.object.isRequired,

  /**
   * Subject owns the role assignment
   */
  subject: PropTypes.object,

  /**
   * Callback to set the status back to caller.
   */
  setNotificationInfo: PropTypes.func.isRequired,

  /**
   * Function to trigger edit role details modal
   */
  onSetOpen: PropTypes.func.isRequired,

  /**
   * callback function to refresh parent when edit succeeded
   */
  refreshParent: PropTypes.func,

  /**
   * callback function to refresh parent when edit succeeded
   */
  userRAs: PropTypes.array
}

export default EditRoleAssignmentSidePanel
